import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from "lodash";
import React, { useContext, useEffect, useState } from "react";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { toast } from "react-toastify";
import Switch from "react-switch";
import { Button, Col, Row, Table } from "reactstrap";
import { CustomerTypeahead, ItemDropdownMenu, ItemTypeahead, ServiceThreadSpinner, SortHeader, Pager } from ".";
import { DesktopContainer } from "../layouts";
import { api, constants, dateHelpers, helpers, UserContext } from "../utils";

export default function ItemPriceReport() {
   const { currentUser } = useContext(UserContext);
   const [initialized, setInitialized] = useState(false);
   const [loaded, setLoaded] = useState(false);
   const [salespeopleList, setSalespeopleList] = useState(null);
   const [classList, setClassList] = useState([]);
   const [data, setData] = useState([]);
   const [downloading, setDownloading] = useState(false);
   const [downloadingPdf, setDownloadingPdf] = useState(false);
   const [page, setPage] = useState(1);
   const [totalPages, setTotalPages] = useState(0);
   const defaultSelect = { label: "--- Select ---", value: 0 };
   const [screenForm, setScreenForm] = useState({
      payload: {
         customerId: defaultSelect, //its a select
         itemNumber: defaultSelect, //its a select
         classList: [], //its an array, but will be turnded into a string
         salesPersonId: defaultSelect, //its a select
         twelveMonthsOnly: true, //its a switch
      },
      pageController: {
         pageNumber: page,
         pageSize: 25,
      },
      sortController: {
         sortDirection: "asc",
         sortField: "ItemNumber",
      },
   });

   useEffect(() => {
      document.title = `Machine Work Order Processing`;
      if (currentUser && !initialized) {
         if (currentUser.customerFilter) {
            currentUser.setCustomerFilter(null);
         }
      }

      let apiCalls = [];
      apiCalls.push(
         api
            .fetch(`Reference/GetSalespeopleList`)
            .then((response) => {
               return {
                  salespeople: response.data.salespeople,
               };
            })
            .catch(api.catchHandler)
      );
      apiCalls.push(
         api
            .fetch(`Reference/GetItemClasses`)
            .then((response) => {
               return {
                  itemClasses: _.map(response.data.itemClasses, (x) => {
                     return {
                        value: x.itemClass,
                        label: `${x.itemClass} | ${x.itemClassDescription}`,
                     };
                  }),
               };
            })
            .catch(api.catchHandler)
      );
      Promise.all(apiCalls)
         .then((arrayResults) => {
            let aggResults = {};
            _.each(arrayResults, (x) => Object.assign(aggResults, x));
            setSalespeopleList(
               _.chain(aggResults.salespeople)
                  .map((sp) => {
                     return {
                        value: sp.salespersonId,
                        label: sp.salespersonText,
                     };
                  })
                  .sortBy((x) => {
                     return x.label;
                  })
                  .unshift({
                     value: "All",
                     label: "--- All ---",
                  })
                  .value()
            );
            setClassList(aggResults.itemClasses);
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error("There was an error loading sales people data");
         })
         .finally(() => setInitialized(true));
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   useEffect(() => {
      if (initialized && !loaded && !downloading) {
         runQuery();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [initialized, loaded, downloading, currentUser]);

   function createPayload() {
      const payloadOnState = _.cloneDeep(screenForm);
      let payload = Object.assign(
         {
            customerId: helpers.valueFromSelect(
               payloadOnState.payload.customerId,
               true
            ), //its a select
            itemNumber: helpers.valueFromSelect(
               payloadOnState.payload.itemNumber,
               true
            ), //its a select
            classList: helpers.getArrayOfStrings(payloadOnState.payload.classList), //its an array, but will be turnded into a string
            salesPersonId: helpers.valueFromSelect(
               payloadOnState.payload.salesPersonId,
               true
            ), //its a select
            twelveMonthsOnly: payloadOnState.payload.twelveMonthsOnly, //its a switch
         },
         payloadOnState.pageController,
         payloadOnState.sortController
      );
      if (payload.startDate === "") {
         payload.startDate = null;
      }
      if (payload.endDate === "") {
         payload.endDate = null;
      }
      return payload;
   }

   function downloadPriceMarginPdf() {
      let itemNumber = helpers.valueFromSelect(screenForm.payload.itemNumber);
      setDownloadingPdf(true);
      api
         .postDownloadBinary(
            `Report/SalesItemPriceMargin`,
            createPayload(),
            constants.MIME_TYPES.pdf,
            `Sale_Item_Price_${itemNumber}_${dateHelpers.toLongDateString(
               Date.now
            )}.pdf`,
            "There was an error downloading the Item Price Margin Report pdf."
         )
         .then((res) => {
            if (res?.data === false) {
               toast.error(res.message);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setDownloadingPdf(false));
   }

   function exportXlsx() {
      setDownloading(true);
      api
         .postGetBlob("Sales/ExportSalesItemPriceMarginXLSX", createPayload())
         .then((response) => {
            setDownloading(false);
            if (response && response.data) {
               if (response.data.success === false) {
                  if (_.isString(response?.data?.message)) {
                     toast.error(response?.data?.message);
                  }
                  return;
               }
               if (response.data) {
                  const XLSX_FILENAME = "Item_Price_Margins.xlsx";
                  helpers.downloadExport(
                     response.data,
                     XLSX_FILENAME,
                     constants.MIME_TYPES.xlsx
                  );
               }
            } else {
               toast.error(response.message || "There was an error downloading the Item Price Margin Report");
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error("There was an error downloading the Item Price Margin Report");
         })
         .finally(() => setDownloading(false));
   }

   const onChange = (property, value, optionalDirectionOnly = null) => {
      if (property === "payload.salesPersonId" && value.value === "All")
         value.value = "";

      if (property === "payload.customerId" && (value === null || value === undefined)) {
         value = defaultSelect;
         value.value = "";
      }
      //console.log(property + ": " + JSON.stringify(value));

      let copy = _.cloneDeep(screenForm);
      if (optionalDirectionOnly != null)
         _.set(copy, "sortController.sortDirection", optionalDirectionOnly);

      _.set(copy, property, value);

      if (property !== "sortController.sortField"
         && property !== "pageController.pageSize"
         && property !== "pageController.pageNumber")
         _.set(copy, "pageController.pageNumber", 1);

      setScreenForm(copy);
      setLoaded(false);
      return;
   };

   function onPageChange(page) {
      setPage(page);
      setLoaded(false);
   }

   function onPageSizeChange(size) {
      onChange("pageController.pageSize", size);
      setTotalPages(null);
      onPageChange(1);
   }

   function runQuery() {
      api
         .post(`Sales/LoadSalesItemPriceMarginReport`, createPayload())
         .then((response) => {
            if (response.data.success) {
               setData(response.data.newOrdersData.items);
               setTotalPages(
                  _.ceil(
                     response.data.newOrdersData.totalCount /
                     screenForm.pageController.pageSize
                  )
               );
            }
         })
         .catch(api.catchHandler)
         .finally(() => setLoaded(true));
   }

   let alreadyDisplayedList = [];
   return (
      <DesktopContainer screenName="Item Price Margin Report">
         <div className="filter-header">
            <Row className="justify-content-center">
               <Col xs={8} className="justify-content-center">
                  <Row className="align-items-center my-1">
                     <Col className="text-end text-white h5" xs={2}>
                        Classes:
                     </Col>
                     <Col xs={8}>
                        <Select
                           classNamePrefix="serviceThreadSelect"
                           closeMenuOnSelect={true}
                           isMulti
                           components={makeAnimated()}
                           options={classList}
                           onChange={(opt) => onChange("payload.classList", opt)}
                           isClearable
                        />
                     </Col>
                  </Row>

                  <Row className="align-items-center my-1">
                     <Col className="text-end text-white h5" xs={2}>
                        Item #:
                     </Col>
                     <Col xs={8}>
                        <ItemTypeahead
                           value={screenForm.payload.itemNumber}
                           isClearable
                           onChange={(opt) => { onChange("payload.itemNumber", opt); }}
                        />
                     </Col>
                  </Row>
                  {!currentUser?.isAnyCustomerTypeRole && (
                     <Row className="align-items-center my-1">
                        <Col className="text-end text-white h5" xs={2}>
                           Customer:
                        </Col>
                        <Col xs={8}>
                           <CustomerTypeahead
                              value={screenForm.payload.customerId}
                              onChange={(opt) => { onChange("payload.customerId", opt); }}
                           />
                        </Col>
                     </Row>
                  )}
                  <Row className="align-items-center my-1">
                     <Col className="text-end text-white h5" xs={2}>
                        Sales Person:
                     </Col>
                     <Col xs={8}>
                        <Select
                           classNamePrefix="serviceThreadSelect"
                           closeMenuOnSelect={true}
                           components={makeAnimated()}
                           options={salespeopleList}
                           onChange={(opt) => onChange("payload.salesPersonId", opt)}
                           value={screenForm.payload.salesPersonId}
                           isClearable
                        />
                     </Col>
                  </Row>
                  <Row>
                     <Col xs={2} className="text-end">
                        <Switch
                           onChange={() => onChange("payload.twelveMonthsOnly", !screenForm.payload.twelveMonthsOnly)}
                           checked={screenForm.payload.twelveMonthsOnly}
                        />
                     </Col>
                     <Col className=" text-white h5" xs={8}>
                        Only show items with sales activity in the past 12 months?
                     </Col>
                  </Row>
               </Col>
            </Row>
         </div>
         {data.length ? (
            <Row className="px-2">
               <Row className="align-items-center mt-4 mb-2">
                  <Col className="transparent-background">
                     <Pager
                        // callBack={(newPageNumber) => setScreenForm({...screenForm, pageController: {pageNumber: newPageNumber, pageSize: screenForm.pageController.pageSize}})}
                        callBack={(newPageNumber) => onChange("pageController.pageNumber", newPageNumber)}
                        sizeCallback={onPageSizeChange}
                        pageNumber={screenForm.pageController.pageNumber}
                        pageSize={screenForm.pageController.pageSize}
                        totalPages={totalPages}
                        downloading={downloading}
                     />
                  </Col>
                  <Col>
                     {data && data.length ? (
                        <Button
                           title="Download as .xlsx"
                           className="shredButtonExcel float-end ms-2"
                           onClick={exportXlsx}
                           disabled={downloading}
                        >
                           <FontAwesomeIcon icon="file-excel" size="lg" />
                        </Button>
                     ) : null}
                     {data && data.length ? (
                        <Button
                           title="Download Price Margin Report PDF"
                           className="shredButton float-end ms-2"
                           onClick={downloadPriceMarginPdf}
                           disabled={downloadingPdf}
                        >
                           Run Price Report
                        </Button>
                     ) : null}
                  </Col>
               </Row>
               <ServiceThreadSpinner on={!loaded}>
                  <Table striped className="data-table">
                     <thead className="table-header">
                        <tr>
                           <th>
                              <SortHeader
                                 displayName="Item"
                                 field="ItemNumber"
                                 sortDirection={screenForm.sortController.sortDirection}
                                 sorted={screenForm.sortController.sortField === "ItemNumber"}
                                 callback={(fieldName, direction) => onChange("sortController.sortField", fieldName, direction)}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Customer"
                                 field="CustomerName"
                                 sortDirection={screenForm.sortController.sortDirection}
                                 sorted={screenForm.sortController.sortField === "CustomerName"}
                                 callback={(fieldName, direction) => onChange("sortController.sortField", fieldName, direction)}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Sales Person"
                                 field="SalesPerson"
                                 sortDirection={screenForm.sortController.sortDirection}
                                 sorted={screenForm.sortController.sortField === "SalesPerson"}
                                 callback={(fieldName, direction) => onChange("sortController.sortField", fieldName, direction)}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Customer Part Number"
                                 field="CustomerItemNumber"
                                 sortDirection={screenForm.sortController.sortDirection}
                                 sorted={screenForm.sortController.sortField === "CustomerItemNumber"}
                                 callback={(fieldName, direction) => onChange("sortController.sortField", fieldName, direction)}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Price Level"
                                 field="PriceLevel"
                                 sortDirection={screenForm.sortController.sortDirection}
                                 sorted={screenForm.sortController.sortField === "PriceLevel"}
                                 callback={(fieldName, direction) => onChange("sortController.sortField", fieldName, direction)}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Selling Price"
                                 field="SellingPrice"
                                 sortDirection={screenForm.sortController.sortDirection}
                                 sorted={screenForm.sortController.sortField === "SellingPrice"}
                                 callback={(fieldName, direction) => onChange("sortController.sortField", fieldName, direction)}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Cost"
                                 field="TotalCost"
                                 sortDirection={screenForm.sortController.sortDirection}
                                 sorted={screenForm.sortController.sortField === "TotalCost"}
                                 callback={(fieldName, direction) => onChange("sortController.sortField", fieldName, direction)}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="Profit Margin"
                                 field="ProfitMargin"
                                 sortDirection={screenForm.sortController.sortDirection}
                                 sorted={screenForm.sortController.sortField === "ProfitMargin"}
                                 callback={(fieldName, direction) => onChange("sortController.sortField", fieldName, direction)}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="% Markup"
                                 field="MarkUpPercent"
                                 sortDirection={screenForm.sortController.sortDirection}
                                 sorted={screenForm.sortController.sortField === "MarkUpPercent"}
                                 callback={(fieldName, direction) => onChange("sortController.sortField", fieldName, direction)}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="12 Mos Qty"
                                 field="RollingQtySold"
                                 sortDirection={screenForm.sortController.sortDirection}
                                 sorted={screenForm.sortController.sortField === "RollingQtySold"}
                                 callback={(fieldName, direction) => onChange("sortController.sortField", fieldName, direction)}
                              />
                           </th>
                           <th>
                              <SortHeader
                                 displayName="12 Mos Sales"
                                 field="RollingSalesAmt"
                                 sortDirection={screenForm.sortController.sortDirection}
                                 sorted={screenForm.sortController.sortField === "RollingSalesAmt"}
                                 callback={(fieldName, direction) => onChange("sortController.sortField", fieldName, direction)}
                              />
                           </th>
                        </tr>
                     </thead>
                     <tbody>
                        {_.map(data, (datum, index) => {
                           const alreadyDisplayed = !_.includes(
                              alreadyDisplayedList,
                              datum.itemWithDesc
                           );
                           alreadyDisplayedList.push(datum.itemWithDesc);
                           return (
                              <tr key={`${index}-abc`} className={`tr-${index % 2 === 0 ? "primary" : "secondary"}`} >
                                 {!currentUser?.isAnyCustomerTypeRole &&
                                    alreadyDisplayed ? (
                                    <ItemDropdownMenu
                                       itemDescription={datum.itemWithDesc}
                                       itemNumber={datum.itemNumber}
                                       customerId={datum.customerId}
                                       attachmentCount={datum.itemAttachmentCount}
                                    />
                                 ) : (
                                    <td>{""}</td>
                                 )}
                                 <td
                                    className="data-cell "
                                    children={
                                       <a rel="noreferrer"
                                          href={`/customer-dashboard/${datum.customerNumber}`}
                                          target="_blank"
                                          className="styledAnchor"
                                          style={{ textAlign: "start" }}
                                       >
                                          {datum.customerNumber} | {datum.customerName}
                                       </a>
                                    }
                                 />
                                 <td className="data-cell text-black-white" children={datum.salesPerson} />
                                 <td className="data-cell" children={datum.customerItemNumber} />
                                 <td className="data-cell" children={datum.priceLevel} />
                                 <td className="data-cell" children={helpers.formatCurrency(datum.sellingPrice)} />
                                 <td className="data-cell" children={helpers.formatCurrency(datum.totalCost)} />
                                 <td className="data-cell" children={helpers.formatCurrency(datum.profitMargin)} />
                                 <td className="data-cell"
                                    children={`${helpers.roundNum(datum.markUpPercent * 100).toLocaleString()}%`} />
                                 <td className="data-cell"
                                    children={helpers.formatNumberWithThousands(datum.rollingQtySold)} />
                                 <td className="data-cell"
                                    children={helpers.formatCurrency(datum.rollingSalesAmt)} />
                              </tr>
                           );
                        })}
                     </tbody>
                     <tfoot className="table-footer">
                        <tr>
                           <td />
                           <td />
                           <td />
                           <td />
                           <td />
                           <td />
                           <td />
                           <td />
                           <td />
                           <td />
                           <td />
                        </tr>
                     </tfoot>
                  </Table>
               </ServiceThreadSpinner>
            </Row>
         ) : (
            <div className="h5 text-center mt-3">No data found</div>
         )}
      </DesktopContainer>
   );
}
